import React, { ChangeEvent, MouseEvent } from 'react';
import { Box, Button, InputLabel } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { TOAST_STATUS, toastActions } from '@leagueplatform/toast-messages';
import { Avatar } from '@leagueplatform/web-common-components';
import { useContentBlobUrl } from '@leagueplatform/league-content-api';
import {
  EVENT_NAME,
  PRODUCT_AREA,
  trackAnalyticsEvent,
} from '@leagueplatform/analytics';
import {
  usePersonalInformation,
  useUpdateUserProfile,
} from '../../hooks/use-personal-information.hook';
import { useUploadProfilePhoto } from '../../hooks/use-upload-profile-photo.hook';
import { ANALYTICS_SCREEN_NAMES, DETAIL } from '../../common/constants';

type EditPhotoProps = {
  defaultProfileImg: string;
  alt: string;
  screenName?: string;
};

export function EditProfilePhoto({
  defaultProfileImg,
  alt,
  screenName,
}: EditPhotoProps) {
  const { formatMessage } = useIntl();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const avatar = (
    <Avatar
      imageSrc={defaultProfileImg}
      alt={alt}
      size={128}
      backgroundColor="transparent"
    />
  );

  const { data } = usePersonalInformation();

  const [
    { profilePhotoId, uploadFileIsLoading },
    { uploadProfilePhoto, resetProfilePhotoId },
  ] = useUploadProfilePhoto({
    avatarImageId: data?.userProfile?.avatar?.imageId,
  });

  const { updateUserProfile } = useUpdateUserProfile();

  const blob = useContentBlobUrl(profilePhotoId);

  const handleProfilePhotoClick = () => {
    // Determine if this is an add or edit action

    const detail = blob ? DETAIL.CHANGE_PHOTO : DETAIL.ADD_PHOTO;

    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: PRODUCT_AREA.SETTINGS,
      screen_name: screenName,
      detail,
    });
    inputRef.current?.click();
  };

  return (
    <Box css={{ position: 'relative' }}>
      <Box
        data-testid="profile-image"
        className={profilePhotoId ? 'background-image' : ''}
        css={{
          width: 128,
          height: 128,
          borderRadius: '$circle',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          typography: '$headingOne',
          ...(profilePhotoId && {
            backgroundImage: `url(${blob})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '50%',
          }),
        }}
      >
        {!profilePhotoId && avatar}
      </Box>
      <Button
        loading={uploadFileIsLoading}
        hideLabel
        icon="interfaceEdit"
        size="small"
        css={{
          position: 'absolute',
          bottom: 0,
          right: '$half',
          borderRadius: '$circle',
        }}
        onClick={handleProfilePhotoClick}
      >
        <InputLabel htmlFor="avatar" hideLabel>
          {formatMessage({
            id: defaultProfileImg
              ? 'STR_UPDATE_AVATAR_PHOTO'
              : 'STR_ADD_PROFILE_AVATAR',
          })}
        </InputLabel>
      </Button>
      <input
        id="avatar"
        name="avatar"
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={async (e: ChangeEvent<HTMLInputElement>) => {
          if (!e.currentTarget.files) return;
          const file = e?.currentTarget?.files[0];
          if (file) {
            const newID = await uploadProfilePhoto(file);
            if (newID) {
              updateUserProfile({
                avatar: { imageId: newID },
              });
              toastActions.add({
                type: TOAST_STATUS.SUCCESS,
                textId: formatMessage({ id: 'STR_AVATAR_PHOTO_UPDATED' }),
              });
            } else {
              resetProfilePhotoId();
              trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
                product_area: screenName,
                screen_name: ANALYTICS_SCREEN_NAMES.ERROR_UPLOADING_PHOTO_PHOTO,
              });
              toastActions.add({
                type: TOAST_STATUS.ERROR,
                textId: formatMessage({ id: 'STR_AVATAR_PHOTO_UPDATE_FAILED' }),
              });
            }
          }
        }}
        onClick={(e: MouseEvent<HTMLInputElement>) => {
          (e.target as HTMLInputElement).value = '';
        }}
      />
    </Box>
  );
}
